import React, { memo } from 'react'
import { generatePath, NavLink } from 'react-router-dom'
import { IconName } from '@unbounded/unbounded-components'
import * as Styled from './Breadcrumbs.styles'

interface Props {
  title: string
  path?: string
  params?: Record<string, string | undefined>
}

/*
   Usage example:

   <Breadcrumbs>
     <Breadcrumb title="Datasets" path={Paths.datasets} />
     <Breadcrumb title="Update a dataset" path={Paths.datasetDetails} params={useParams()} />
   </Breadcrumbs>
 */
export const Breadcrumb: React.FC<Props> = () => null

const Breadcrumbs: React.FC = memo(({ children: propsChildren }) => {
  const children = []

  if (!propsChildren) {
    return null
  }

  if (!Array.isArray(propsChildren)) {
    children.push(propsChildren)
  } else {
    children.push(...propsChildren)
  }

  const childProps = children
    .filter(rawChild => {
      const child = rawChild as { type: unknown; props: Props }

      return child.type === Breadcrumb && child.props
    })
    .map(rawChild => {
      const child = rawChild as { type: unknown; props: Props }

      return child.props
    })

  const renderProps = childProps.map(childProp => {
    const newProp = { ...childProp }

    if (!newProp.path || !newProp.params) {
      return newProp
    }
    newProp.path = generatePath(newProp.path, newProp.params)
    if (newProp.path.endsWith('/')) {
      newProp.path = newProp.path.substring(0, newProp.path.length - 1)
    }
    return newProp
  })

  return (
    <Styled.BreadcrumbsContainer>
      {renderProps.map(({ title, path }, index) => (
        <Styled.Crumb key={path}>
          {index > 0 && <Styled.Icon name={IconName.chevron_right} />}

          {path ? (
            <Styled.Link as={NavLink} to={path}>
              {title}
            </Styled.Link>
          ) : (
            <Styled.Link>{title}</Styled.Link>
          )}
        </Styled.Crumb>
      ))}
    </Styled.BreadcrumbsContainer>
  )
})

Breadcrumbs.displayName = 'Breadcrumbs'

export default Breadcrumbs
